// settleWithLimit — run an async call over many items, a bounded number at a time.
//
// Counterpart of `settleInTurn` for calls that are safe to overlap: the
// request is made through `dispatch(endpoint.initiate(...))` (see
// `fetchFresh`), which gives every call a subscription of its own, so nothing
// one call does can drop another's cache entry. The cap keeps a large org
// (dozens of banks) from opening one connection per bank at once.

/**
 * How many hydration requests of one kind may be in flight together. The API
 * answers 403 "Not registered as a Liftnet user" to concurrent requests once its
 * per-request user lookup is saturated: on dev, 1 or 2 at a time all succeeded
 * and 3 at a time already lost a third. Stay at 2 until the API stops failing
 * under concurrency.
 */
export const HYDRATE_CONCURRENCY = 2;

/**
 * Call `fetchOne` for every item, at most `limit` at a time, and report every
 * outcome in input order with the shape `Promise.allSettled` gives. A failure
 * of one item never stops the others.
 */
export default async function settleWithLimit<T, R>(
  items: readonly T[],
  limit: number,
  fetchOne: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const settled: PromiseSettledResult<R>[] = new Array(items.length);
  let next = 0;

  const settleOne = (index: number): Promise<void> =>
    fetchOne(items[index]).then(
      (value) => {
        settled[index] = { status: "fulfilled", value };
      },
      (reason) => {
        settled[index] = { status: "rejected", reason };
      },
    );

  // A worker takes the next unclaimed item, and once that settles, takes
  // another, until none is left. `limit` workers run side by side.
  const worker = (): Promise<void> => {
    if (next >= items.length) return Promise.resolve();
    const index = next;
    next += 1;
    return settleOne(index).then(worker);
  };

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => worker()));
  return settled;
}
